import React, { ReactNode } from "react";
import { useIntl } from "react-intl";
import { ConnectedProps, connect, useSelector } from "react-redux";

import * as api from "@nens/threedi/openapi-client";

// creating rasters for testing
import { roundScalarOfMinAndMaxNumbers } from "../utils/rounding";

import { getSelectedModelRaster, setSelectedModelRaster } from "./../state/layerMenu";
import { getMinMaxForLayers } from "./../state/layerRasterSourcesMenu";
import LayerSubMenu from "./LayerSubMenu";

interface Props extends ConnectedProps<typeof connector> {
  raster: api.Raster;
  children?: ReactNode;
}

const LayerMenuModelRasterLayer: React.FC<Props> = (props) => {
  const selectedModelRaster = useSelector(getSelectedModelRaster);
  const minMaxLegendArray = useSelector(getMinMaxForLayers);
  const { raster } = props;
  const intl = useIntl();

  const minMax = minMaxLegendArray.filter((item) => item.layer === raster.type)[0];
  // add guard  in case min max undefined:
  // https://github.com/nens/threedi-live-site/issues/716
  const roundedMinMax = (minMax && roundScalarOfMinAndMaxNumbers(minMax.min, minMax.max)) || {
    min: 0,
    max: 0,
  };
  const roundedMin = roundedMinMax.min;
  const roundedMax = roundedMinMax.max;

  const modelRasterTypeToUserString = (type: string) => {
    const removedUnderscores = type.split("_file")[0].split("_").join(" ");
    if (removedUnderscores === "dem") {
      return intl.formatMessage({
        id: "layerMenuModelRasterLayer.rasterDem",
        defaultMessage: "Elevation",
      });
    }
    if (removedUnderscores === "interception") {
      return intl.formatMessage({
        id: "layerMenuModelRasterLayer.rasterInterception",
        defaultMessage: "Interception",
      });
    }
    if (removedUnderscores === "dem raw") {
      return intl.formatMessage({
        id: "layerMenuModelRasterLayer.dem_raw",
        defaultMessage: "DEM raw",
      });
    }
    if (removedUnderscores === "equilibrium infiltration rate") {
      return intl.formatMessage({
        id: "layerMenuModelRasterLayer.equilibrium_infiltration_rate",
        defaultMessage: "Equilibrium infiltration rate",
      });
    }
    if (removedUnderscores === "frict coef") {
      return intl.formatMessage({
        id: "layerMenuModelRasterLayer.frict_coef",
        defaultMessage: "Friction coefficient",
      });
    }
    if (removedUnderscores === "initial groundwater level") {
      return intl.formatMessage({
        id: "layerMenuModelRasterLayer.initial_groundwater_level",
        defaultMessage: "Initial groundwater level",
      });
    }
    if (removedUnderscores === "initial waterlevel") {
      return intl.formatMessage({
        id: "layerMenuModelRasterLayer.initial_waterlevel",
        defaultMessage: "Initial waterlevel",
      });
    }
    if (removedUnderscores === "groundwater hydro connectivity") {
      return intl.formatMessage({
        id: "layerMenuModelRasterLayer.groundwater_hydro_connectivity",
        defaultMessage: "Groundwater hydro connectivity",
      });
    }
    if (removedUnderscores === "groundwater impervious layer level") {
      return intl.formatMessage({
        id: "layerMenuModelRasterLayer.groundwater_impervious_layer_level",
        defaultMessage: "Groundwater impervious layer level",
      });
    }
    if (removedUnderscores === "infiltration decay period") {
      return intl.formatMessage({
        id: "layerMenuModelRasterLayer.infiltration_decay_period",
        defaultMessage: "Infiltration decay period",
      });
    }
    if (removedUnderscores === "initial infiltration rate") {
      return intl.formatMessage({
        id: "layerMenuModelRasterLayer.initial_infiltration_rate",
        defaultMessage: "Initial infiltration rate",
      });
    }
    if (removedUnderscores === "leakage") {
      return intl.formatMessage({
        id: "layerMenuModelRasterLayer.leakage",
        defaultMessage: "Leakage",
      });
    }
    if (removedUnderscores === "phreatic storage capacity") {
      return intl.formatMessage({
        id: "layerMenuModelRasterLayer.phreatic_storage_capacity",
        defaultMessage: "Phreatic storage capacity",
      });
    }
    if (removedUnderscores === "hydraulic conductivity") {
      return intl.formatMessage({
        id: "layerMenuModelRasterLayer.hydraulic_conductivity",
        defaultMessage: "Hydraulic conductivity",
      });
    }
    if (removedUnderscores === "porosity") {
      return intl.formatMessage({
        id: "layerMenuModelRasterLayer.porosity",
        defaultMessage: "Porosity",
      });
    }
    if (removedUnderscores === "infiltration rate") {
      return intl.formatMessage({
        id: "layerMenuModelRasterLayer.infiltration_rate",
        defaultMessage: "Infiltration rate",
      });
    }
    if (removedUnderscores === "max infiltration capacity") {
      return intl.formatMessage({
        id: "layerMenuModelRasterLayer.max_infiltration_capacity",
        defaultMessage: "Max infiltration capacity",
      });
    }
    return removedUnderscores.charAt(0).toUpperCase() + removedUnderscores.slice(1);
  };

  const getModelRasterUnit = (type: string) => {
    const layer = type.split("_file")[0].split("_").join(" ");
    if (layer === "dem") {
      return "mMSL";
    }
    if (layer === "interception") {
      return "m";
    }
    if (layer === "dem raw") {
      return "mMSL";
    }
    if (layer === "equilibrium infiltration rate") {
      return "mm/day";
    }
    if (layer === "frict coef") {
      //!!!!!NOTE: DEPENDS ON TYPE. for now only manning (CHEZY will be (m^1/2)/s)
      return "s/(m^1/3)";
    }
    if (layer === "initial groundwater level") {
      return "mMSL";
    }
    if (layer === "initial waterlevel") {
      return "mMSL";
    }
    if (layer === "groundwater hydro connectivity") {
      return "m/day";
    }
    if (layer === "groundwater impervious layer level") {
      return "mMSL";
    }
    if (layer === "infiltration decay period") {
      return "mm/day";
    }
    if (layer === "initial infiltration rate") {
      return "mm/day";
    }
    if (layer === "leakage") {
      return "mm/day";
    }
    if (layer === "phreatic storage capacity") {
      return "-";
    }
    if (layer === "hydraulic conductivity") {
      return "m/day";
    }
    if (layer === "porosity") {
      return "-";
    }
    if (layer === "infiltration rate") {
      return "mm/day";
    }
    if (layer === "max infiltration capacity") {
      return "m";
    }
    return "";
  };

  const unit = getModelRasterUnit(raster.type);

  return (
    <LayerSubMenu.Item
      active={selectedModelRaster === raster.type}
      title={minMax ? `min: ${roundedMin} | max: ${roundedMax} | unit: ${unit}` : undefined}
      onClick={() => {
        selectedModelRaster === raster.type
          ? props.setSelectedModelRaster("")
          : props.setSelectedModelRaster(raster.type);
      }}
    >
      <LayerSubMenu.Item.Name>{modelRasterTypeToUserString(raster.type)}</LayerSubMenu.Item.Name>
      <LayerSubMenu.Item.Legend>
        <LayerSubMenu.Item.Legend.Gradient
          title={minMax ? `min: ${roundedMin} | max: ${roundedMax} | unit: ${unit}` : undefined}
        >
          <LayerSubMenu.Item.Legend.Gradient.Image
            src={(() => {
              if (raster.type === api.RasterTypeEnum.DEM_FILE) {
                return "./assets/images/raster_legend_bathemetry.png";
              }
              if (raster.type === api.RasterTypeEnum.INTERCEPTION_FILE) {
                return "./assets/images/raster_legend_interception.png";
              }
              return "./assets/images/raster_legend_default.png";
            })()}
            alt={(() => {
              if (raster.type === api.RasterTypeEnum.DEM_FILE) {
                return "DEM raster legend";
              }
              if (raster.type === api.RasterTypeEnum.INTERCEPTION_FILE) {
                return "interception raster legend";
              }
              return "default raster legend";
            })()}
          />
          {/* {minMax && minMax.max} */}
        </LayerSubMenu.Item.Legend.Gradient>
      </LayerSubMenu.Item.Legend>
    </LayerSubMenu.Item>
  );
};

const connector = connect(null, {
  setSelectedModelRaster,
});
export default connector(LayerMenuModelRasterLayer);
